import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { DefectWithDetails } from "@/hooks/useDefectsQuery";
import { useAuth } from "@/hooks/useAuth";
import { useSapPushMangel } from "@/hooks/useSapPushMangel";

type SapSyncStatus = DefectWithDetails["sap_sync_status"];

const STATUS_STYLES: Record<SapSyncStatus, string> = {
  pending: "bg-muted text-muted-foreground",
  success: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  error: "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300",
};

/**
 * TES-545 — "SAP-Integration" section in the defect detail view.
 *
 * Shows the persisted SAP Q-Meldung number + the sap_sync_status badge, and (only for the operative
 * SAP roles qualitaetsmanager / mangelbearbeiter, mirroring the role_permissions push_mangel grant in
 * migration 296) a "Q-Meldung pushen" button that enqueues a SAP push via sap-integrations/push-mangel.
 *
 * Real schema is law: defects carry no machine_id, so the LH "disable on missing Kommissionsnummer"
 * rule is N/A here; the implementable client-side precondition is title + description present, and the
 * Edge Function performs the authoritative validation.
 */
export function SapIntegrationCard({ defect }: { defect: DefectWithDetails }) {
  const { t } = useTranslation();
  const { hasRole } = useAuth();
  const push = useSapPushMangel(defect.id);

  const canPush = hasRole("qualitaetsmanager") || hasRole("mangelbearbeiter");
  const missingRequiredFields = !defect.title?.trim() || !defect.description?.trim();
  const status = defect.sap_sync_status;
  // Only an in-flight push disables the button. Note: sap_sync_status defaults to "pending" for every
  // never-pushed defect (Mig. 296), so "pending" must NOT disable — that is the normal push/re-push state.
  const isBusy = push.isPending;

  const handlePush = () => {
    push.mutate(undefined, {
      onSuccess: (result) => {
        toast.success(
          result.duplicate
            ? t("defects.detail.sap.pushDuplicate")
            : t("defects.detail.sap.pushSuccess"),
        );
      },
      onError: () => {
        toast.error(t("defects.detail.sap.pushError"));
      },
    });
  };

  return (
    <section
      data-testid="sap-integration-card"
      className="bg-card border border-outline-variant rounded-lg p-6"
    >
      <h3 className="text-sm font-semibold text-foreground mb-4">
        {t("defects.detail.sap.title")}
      </h3>

      <dl className="space-y-3 text-sm">
        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">{t("defects.detail.sap.qmeldungLabel")}</dt>
          <dd className="font-medium text-foreground" data-testid="sap-qmeldung-nr">
            {defect.sap_qmeldung_nr?.trim() || t("defects.detail.sap.noQmeldung")}
          </dd>
        </div>

        <div className="flex items-center justify-between gap-3">
          <dt className="text-muted-foreground">{t("defects.detail.sap.statusLabel")}</dt>
          <dd>
            <span
              data-testid="sap-sync-status"
              className={cn(
                "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
                STATUS_STYLES[status],
              )}
            >
              {t(`defects.detail.sap.status.${status}`)}
            </span>
          </dd>
        </div>
      </dl>

      {canPush && (
        <div className="mt-4">
          <Button
            type="button"
            size="sm"
            className="w-full"
            data-testid="sap-push-button"
            disabled={isBusy || missingRequiredFields}
            onClick={handlePush}
          >
            <Send className="mr-2 h-4 w-4" aria-hidden="true" />
            {isBusy ? t("defects.detail.sap.pushing") : t("defects.detail.sap.pushButton")}
          </Button>
          {missingRequiredFields && (
            <p className="mt-2 text-xs text-muted-foreground" data-testid="sap-push-hint">
              {t("defects.detail.sap.missingFields")}
            </p>
          )}
          {push.isError && (
            <div
              role="alert"
              data-testid="sap-push-error"
              className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <span>{t("defects.detail.sap.pushError")}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid="sap-push-retry-button"
                  disabled={isBusy || missingRequiredFields}
                  onClick={handlePush}
                >
                  {t("defects.detail.sap.retry")}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
